import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';

import { ColumnHeader, ErrorGroup } from '../../types';

export type UploadErrorDetailsTableProps = {
  errorGroups: Array<ErrorGroup>;
  columnHeaders: Array<ColumnHeader>;
};

export const UploadErrorDetailsTable: FC<UploadErrorDetailsTableProps> = ({ errorGroups, columnHeaders }) => {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const toggleGroup = (groupIndex: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupIndex)) {
        next.delete(groupIndex);
      } else {
        next.add(groupIndex);
      }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {errorGroups.map(({ columnLabel, errorCount, rows }, groupIndex) => {
        const isExpanded = expandedGroups.has(groupIndex);
        return (
          <div key={groupIndex} className="overflow-hidden rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => toggleGroup(groupIndex)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <ChevronRightIcon
                  className={cn('size-5 text-gray-500 transition-transform duration-200', isExpanded && 'rotate-90')}
                />
                <h4 className="text-sm font-semibold text-gray-900">{columnLabel}</h4>
              </div>
              <span className="text-sm text-gray-500">
                {t('common:components.uploadErrorDetailsTable.errorCount', { count: errorCount })}
              </span>
            </button>
            {isExpanded && (
              <div className="max-h-96 overflow-y-auto border-t border-gray-200 bg-white">
                <table className="min-w-full table-fixed">
                  <thead className="sticky top-0 z-10 bg-red-50">
                    <tr>
                      {columnHeaders.map((header, index) => (
                        <th
                          key={index}
                          scope="col"
                          className={cn(
                            'border-b border-red-200 px-4 py-3 text-left text-xs font-medium tracking-wider text-red-800 uppercase',
                            header.className,
                          )}
                        >
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b border-red-200 last:border-b-0">
                        {row.columns.map((column, columnIndex) => (
                          <td
                            key={columnIndex}
                            className={cn(
                              'px-4 py-3 text-sm text-gray-900',
                              columnIndex === 0 && 'whitespace-nowrap',
                              columnHeaders[columnIndex]?.className,
                            )}
                          >
                            {column.value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
